<template>
  <span class="aliyun-captcha-guard">
    <span :id="elementId"></span>
    <button
      :id="buttonId"
      class="aliyun-captcha-guard__trigger"
      type="button"
      tabindex="-1"
      aria-hidden="true"
    ></button>
  </span>
</template>

<script>
import captchaLogoImg from '@/assets/calogo.png'
import {
  initAliyunCaptcha,
  isAliyunCaptchaConfigured,
  resetAliyunCaptcha
} from '@/utils/aliyunCaptcha'

export default {
  name: 'AliyunCaptchaGuard',
  props: {
    cacheKey: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: ''
    },
    logo: {
      type: String,
      default: captchaLogoImg
    }
  },
  data() {
    const guardId = `aliyun-captcha-guard-${Date.now()}-${Math.random().toString(36).slice(2)}`

    return {
      guardId,
      captchaInstance: null,
      captchaInitPromise: null,
      activeExecution: null,
      needsRefresh: false
    }
  },
  computed: {
    elementId() {
      return `${this.guardId}-element`
    },
    buttonId() {
      return `${this.guardId}-button`
    },
    captchaCacheKey() {
      return this.cacheKey || this.guardId
    },
    captchaOptions() {
      return {
        cacheKey: this.captchaCacheKey,
        element: `#${this.elementId}`,
        button: `#${this.buttonId}`
      }
    }
  },
  mounted() {
    window.addEventListener('offline', this.handleOffline)
    window.addEventListener('online', this.handleOnline)
  },
  beforeDestroy() {
    window.removeEventListener('offline', this.handleOffline)
    window.removeEventListener('online', this.handleOnline)
    this.activeExecution = null
    this.destroyCaptcha()
  },
  methods: {
    getCaptchaLanguage() {
      if (this.language) return this.language
      return this.$i18n && this.$i18n.locale === 'tw' ? 'tw' : 'en'
    },
    createError(code, message) {
      const error = new Error(message)
      error.code = code
      return error
    },
    prepareCaptcha() {
      if (!isAliyunCaptchaConfigured()) {
        return Promise.reject(this.createError('CAPTCHA_NOT_CONFIGURED', 'Captcha is not configured'))
      }
      if (this.captchaInstance) return Promise.resolve(this.captchaInstance)
      if (this.captchaInitPromise) return this.captchaInitPromise

      this.captchaInitPromise = initAliyunCaptcha({
        ...this.captchaOptions,
        language: this.getCaptchaLanguage(),
        captchaLogoImg: this.logo,
        captchaVerifyCallback: this.handleCaptchaVerify,
        onBizResultCallback: this.handleBizResult,
        fail: this.handleCaptchaFail,
        // showErrorTip: true,
        getInstance: (instance) => {
          this.captchaInstance = instance
        },
        onError: this.onErrorCaptcha
      }).then((instance) => {
        this.captchaInstance = instance
        this.captchaInitPromise = null
        return instance
      }).catch((error) => {
        this.captchaInitPromise = null
        throw error
      })

      return this.captchaInitPromise
    },
    openCaptcha(instance = this.captchaInstance) {
      if (this.needsRefresh) {
        resetAliyunCaptcha(this.captchaOptions, instance)
        this.needsRefresh = false
      }

      const openMethod = ['show', 'showCaptcha', 'open'].find((method) => {
        return instance && typeof instance[method] === 'function'
      })

      if (openMethod) {
        instance[openMethod]()
        return
      }

      this.$nextTick(() => {
        const button = document.getElementById(this.buttonId)
        if (button) button.click()
      })
    },
    // 驗證成功後執行傳入的業務請求，並將完整回調參數原樣交給請求方。
    execute(requestCallback, options = {}) {
      if (typeof requestCallback !== 'function') {
        return Promise.reject(this.createError('INVALID_REQUEST_CALLBACK', 'Request callback must be a function'))
      }

      if (this.activeExecution) {
        this.prepareCaptcha().then((instance) => this.openCaptcha(instance)).catch(() => {})
        return this.activeExecution.promise
      }

      let resolveExecution
      let rejectExecution
      const executionPromise = new Promise((resolve, reject) => {
        resolveExecution = resolve
        rejectExecution = reject
      })

      this.activeExecution = {
        requestCallback,
        options,
        response: undefined,
        resolve: resolveExecution,
        reject: rejectExecution,
        promise: executionPromise
      }

      this.prepareCaptcha().then((instance) => {
        if (this.activeExecution) this.openCaptcha(instance)
      }).catch((error) => {
        this.rejectActiveExecution(error)
      })

      return executionPromise
    },
    normalizeVerificationResult(response, options) {
      if (typeof options.resolveResult === 'function') {
        const result = options.resolveResult(response) || {}
        return {
          captchaResult: result.captchaResult === true,
          bizResult: result.bizResult === true
        }
      }

      const responseData = response && response.data !== undefined ? response.data : response
      const businessSuccess = typeof options.isSuccess === 'function'
        ? options.isSuccess(response)
        : !responseData || responseData.code === undefined || Number(responseData.code) === 200

      return {
        captchaResult: true,
        bizResult: businessSuccess
      }
    },
    handleCaptchaVerify(captchaVerifyParam) {
      const execution = this.activeExecution
      if (!execution) {
        return Promise.resolve({
          captchaResult: false,
          bizResult: false
        })
      }

      return Promise.resolve().then(() => execution.requestCallback(captchaVerifyParam)).then((response) => {
        if (this.activeExecution === execution) execution.response = response
        return this.normalizeVerificationResult(response, execution.options)
      }).catch((error) => {
        if (this.activeExecution === execution) {
          this.rejectActiveExecution(error)
          this.needsRefresh = true
        }
        return {
          captchaResult: false,
          bizResult: false
        }
      })
    },
    handleBizResult() {
      const execution = this.activeExecution
      if (!execution) return

      this.activeExecution = null
      this.needsRefresh = true
      execution.resolve(execution.response)
    },
    handleCaptchaFail(error) {
      this.rejectActiveExecution(error || this.createError('CAPTCHA_FAILED', 'Captcha verification failed'))
      this.needsRefresh = true
    },
    onErrorCaptcha(errorInfo) {
      console.log('errorInfo', errorInfo)
      const message_error = this.getCaptchaLanguage() == 'tw' ? '驗證失敗，請重試或更換網絡環境' : 'Verification failed. Please try again or switch to a different network.'
      this.$message.error(message_error)
    },
    rejectActiveExecution(error) {
      const execution = this.activeExecution
      if (!execution) return

      this.activeExecution = null
      execution.reject(error)
    },
    handleOffline() {
      console.log("無網絡")
      this.rejectActiveExecution(this.createError('NETWORK_OFFLINE', 'Network is offline'))
      this.destroyCaptcha()
    },
    handleOnline() {
      console.log('网络已恢复')

      // 清理旧验证码实例及缓存
      this.destroyCaptcha()

      // 不立即初始化
      // 等待用户再次点击验证按钮时重新初始化
    },
    // 完整銷毀實例與工具層快取，確保下一次執行重新初始化。
    destroyCaptcha() {
      const instance = this.captchaInstance
      if (instance && typeof instance.hide === 'function') instance.hide()
      if (instance && typeof instance.destroyCaptcha === 'function') instance.destroyCaptcha()

      resetAliyunCaptcha(this.captchaOptions)
      this.captchaInstance = null
      this.captchaInitPromise = null
      this.needsRefresh = false
    }
  }
}
</script>

<style scoped>
.aliyun-captcha-guard {
  display: contents;
}

.aliyun-captcha-guard__trigger {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
}
</style>
